import { compareNames } from '@pubstack/common/src/sort'
import { countStacksUse, StacksBySite } from '@pubstack/common/src/stack'
import { Button } from '@pubstack/ui/components/Button'
import { BaseInput } from '@pubstack/ui/components/Input'
import { Tooltip } from '@pubstack/ui/components/Tooltip'
import { cn } from '@pubstack/ui/utils/cn'
import { FunctionComponent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { Flyout } from '~/components/Flyout'
import { StackStatus } from '~/modules/pbs/sites/site/stacks/StackStatus'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { countMediasUse, pluralize } from './StackUsage'

type StackUse = StacksBySite[string][number] & { siteName: string }

/**
 * Why a version cannot be edited from here, in the same cases the media's own stack list refuses it.
 *
 * A live version is changed by creating the next one, so it is only editable while it is the newest
 * version of its stack and serves all of its traffic; an out-of-sync one has an update to take first.
 */
const editRefusalOf = (use: StackUse): string | undefined => {
  // The newer version first: an out-of-sync live version usually has one already, and it is the one to edit.
  if (use.status === 'live' && !use.isLatestVersion) return 'A newer version of this stack is waiting to be deployed: edit that one.'
  if (use.isOutOfSync) return 'This stack is out of sync: create its updated version from the media first.'
  if (use.status === 'live' && use.ratio < 1) return 'A deployment is in progress on this stack.'
  return undefined
}

/** What a version serves, as the deployment center says it: only a live one has traffic. */
const trafficOf = (use: StackUse): string => (use.status === 'live' ? `${Math.round(use.ratio * 100)}%` : 'Not deployed')

type Props = {
  /** The item's name, which titles the list. */
  name: string
  stacksUse?: StacksBySite
  /** Back to the flyout this list was opened from; the cross still closes the whole thing. */
  onBack: () => void
}

/* One line a row, however long a site's name: dense enough to scan a hundred rows, and a row that wraps
   reads as two. Fixed columns, so a long name is cut short (whole on hover) rather than widening the table. */
const cell = 'truncate border-0 border-b border-solid border-slate-100 px-2 py-1 text-left'

/**
 * Every stack an item is used by, one row per stack version, in place of the flyout that counted them.
 *
 * Rendered by the flyout itself rather than opened as a flyout of its own: there is one global flyout,
 * and replacing its content keeps the way back to the item one click away instead of reopening it.
 */
export const StackUsageDetails: FunctionComponent<Props> = ({ name, stacksUse, onBack }) => {
  const [search, setSearch] = useState('')
  const { canEdit, disabledReason } = useScopePermissions()
  const navigate = useNavigate()
  const flyout = useGlobalFlyout()

  const term = search.trim().toLowerCase()
  const rows: StackUse[] = Object.entries(stacksUse ?? {})
    .flatMap(([siteName, stacks]) => stacks.map((stack) => ({ ...stack, siteName })))
    .filter((use) => !term || use.siteName.toLowerCase().includes(term) || use.stackName.toLowerCase().includes(term))
    .sort((a, b) => compareNames(b.siteName, a.siteName) || compareNames(a.stackName, b.stackName) || a.version.localeCompare(b.version, undefined, { numeric: true }))

  /* The two products edit in different places, as everywhere else: a One stack is a page of ad units and
     rules, a Node stack a flyout's worth of settings over its media's stack list, which `?edit=` opens.

     This flyout is closed before leaving rather than by the move: the wrapper closes whatever is open when
     the path changes, and the edit flyout would be what is open by then. */
  const onEdit = async (use: StackUse) => {
    if (use.type === 'adm') {
      navigate(`/monetization/one/sites/${use.siteId}/stacks/${use.stackId}`)
    } else {
      await flyout.close()
      navigate(`/monetization/node/sites/${use.siteId}/stacks?edit=${use.stackId}`)
    }
  }

  return (
    <Flyout.Content>
      <Flyout.Title>
        <span className={'flex items-center gap-2'}>
          <Button variant={'tertiary'} iconName={'chevron_left'} onClick={onBack} trackingLabel={'common-stack-usage-back'} aria-label={'Back'} />
          {name} - used by
        </span>
      </Flyout.Title>
      <Flyout.Body>
        <div className={'flex flex-col gap-4'}>
          <span className={'text-slate-500'}>
            {pluralize(countMediasUse(stacksUse), 'media')} · {pluralize(countStacksUse(stacksUse), 'stack')}
          </span>
          {countStacksUse(stacksUse) === 0 ? (
            <span className={'text-slate-grey'}>Not used by any stack.</span>
          ) : (
            <>
              <BaseInput
                label={'Search a site or a stack'}
                labelIsPlaceholder
                iconLeft={'search'}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                isClearable
                onClear={() => setSearch('')}
              />
              {rows.length === 0 ? (
                <span className={'text-slate-grey'}>No site or stack matches “{search.trim()}”.</span>
              ) : (
                <table className={'w-full table-fixed border-collapse text-sm'}>
                  <thead>
                    <tr className={'text-slate-500'}>
                      <th className={cn(cell, 'font-normal')}>Site</th>
                      <th className={cn(cell, 'w-1/5 font-normal')}>Stack name</th>
                      <th className={cn(cell, 'w-14 font-normal')}>Version</th>
                      <th className={cn(cell, 'w-32 font-normal')}>Status</th>
                      <th className={cn(cell, 'w-20 font-normal')}>Live traffic</th>
                      <th className={cn(cell, 'w-7')}>
                        <span className={'sr-only'}>Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((use) => {
                      const refusal = canEdit ? editRefusalOf(use) : disabledReason
                      return (
                        <tr key={use.stackId}>
                          <td className={cell} title={use.siteName}>
                            {use.siteName}
                          </td>
                          <td className={cell} title={use.stackName}>
                            {use.stackName}
                          </td>
                          <td className={cell}>{use.version === 'To deploy' ? 'Draft' : use.version}</td>
                          <td className={cell}>
                            <StackStatus stack={use} />
                          </td>
                          <td className={cn(cell, use.status !== 'live' && 'text-slate-grey')}>{trafficOf(use)}</td>
                          <td className={cn(cell, 'overflow-visible text-right')}>
                            {/* To the left: the button is the last thing before the flyout's edge. `inline-flex`, as the
                                  trigger is otherwise inline and reserves a text line under the button, growing the row. */}
                            <Tooltip title={refusal ?? 'Edit'} positions={['left']} className={'inline-flex align-middle'}>
                              {/* A small icon button sizes its box for a 16px icon but draws the default 24px one, and
                                    a tertiary one pads 9px above and 4px below: the hover circle sat off the pencil. A
                                    16px icon in even padding is a round 28px target centred on it. */}
                              <Button
                                variant={'tertiary'}
                                size={'s'}
                                iconSize={'xs'}
                                style={{ padding: '6px' }}
                                iconName={'edit'}
                                disabled={!!refusal}
                                onClick={() => onEdit(use)}
                                trackingLabel={'common-stack-usage-edit'}
                                aria-label={'Edit'}
                              />
                            </Tooltip>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </Flyout.Body>
      <Flyout.Actions>
        <Button variant={'secondary'} iconName={'chevron_left'} onClick={onBack} trackingLabel={'common-stack-usage-back'}>
          Back
        </Button>
      </Flyout.Actions>
    </Flyout.Content>
  )
}
